import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import FarmersNav from "@/components/FarmersNav";
import { ArrowLeft, Compass, ArrowRight, MapPin } from "@phosphor-icons/react";

/**
 * FarmersLocal — /farmers/local
 * Geographic drill-down: Country → State → District → City → Village
 * Powered by /farmers/reports/geo-facets and /farmers/reports/local.
 */
export default function FarmersLocal() {
  const [path, setPath] = useState({ country: "", state: "", district: "", city: "", village: "" });
  const [facets, setFacets] = useState({ level: "country", facets: [] });
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(path).forEach(([k, v]) => { if (v) params.set(k, v); });
    Promise.all([
      http.get(`/farmers/reports/geo-facets?${params.toString()}`),
      http.get(`/farmers/reports/local?${params.toString()}&limit=80`),
    ]).then(([f, r]) => { setFacets(f.data); setReports(r.data.reports || []); })
      .catch(() => { setFacets({ level: "country", facets: [] }); setReports([]); })
      .finally(() => setLoading(false));
  };
  useEffect(load, [path.country, path.state, path.district, path.city, path.village]); // eslint-disable-line

  const drill = (level, name) => {
    setPath((p) => ({ ...p, [level]: name }));
  };
  const clearFrom = (level) => {
    const order = ["country", "state", "district", "city", "village"];
    const idx = order.indexOf(level);
    setPath((p) => {
      const next = { ...p };
      for (let i = idx; i < order.length; i++) next[order[i]] = "";
      return next;
    });
  };

  const crumbs = [
    { label: "World", key: null, level: null },
    ...["country", "state", "district", "city", "village"]
      .map((k) => path[k] ? { label: path[k], key: k, level: k } : null)
      .filter(Boolean),
  ];

  return (
    <main className="bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332] min-h-screen pt-32 md:pt-36 pb-32" data-testid="farmers-local-page">
      <FarmersNav />
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 mt-6">
        <Link to="/farmers" className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#1B4332]">
          <ArrowLeft size={11} weight="bold" /> Farmers Pulse
        </Link>
        <div className="mt-3">
          <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-2">
            <Compass size={12} weight="fill" /> / Local Farm Pulse
          </div>
          <h1 className="mt-2 font-serif-display text-4xl md:text-5xl leading-[0.98] tracking-tight">
            Zoom into your community.
          </h1>
          <p className="mt-2 font-serif-display italic text-[#3A3830]">
            Country → State → District → City → Village. See what farmers are observing at every scale.
          </p>
        </div>

        {/* Breadcrumb */}
        <nav className="mt-6 flex items-center gap-2 flex-wrap" data-testid="farmers-local-crumbs">
          {crumbs.map((c, i) => (
            <span key={i} className="inline-flex items-center gap-2">
              <button
                onClick={() => c.level ? clearFrom(c.level) : setPath({ country: "", state: "", district: "", city: "", village: "" })}
                data-testid={`farmers-local-crumb-${c.level || "root"}`}
                className={`rounded-full border px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase ${
                  i === crumbs.length - 1
                    ? "bg-[#1B4332] text-[#F5EBDC] border-black"
                    : "bg-white border-black/15 hover:border-black/40 text-[#3A3830]"
                }`}
              >
                {c.label}
              </button>
              {i < crumbs.length - 1 && <ArrowRight size={11} weight="bold" className="text-[#3A3830]" />}
            </span>
          ))}
        </nav>

        {/* Next-level facets */}
        {facets.level !== "village" && (
          <section className="mt-6 rounded-2xl border border-black/10 bg-white p-6" data-testid="farmers-local-facets">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="font-serif-display text-2xl">
                {facets.level === "country" && "Choose a country"}
                {facets.level === "state" && "Choose a state or province"}
                {facets.level === "district" && "Choose a district or county"}
                {facets.level === "city" && "Choose a city or town"}
              </h2>
              <span className="ml-auto font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">
                {facets.facets?.length || 0} · with observations in past year
              </span>
            </div>
            {loading ? (
              <div className="mt-4 h-16 rounded-lg bg-black/5 animate-pulse" />
            ) : facets.facets?.length === 0 ? (
              <p className="mt-3 text-sm text-[#3A3830]">
                No farmer observations at this level yet. Be the first —{" "}
                <Link to="/farmers/report" className="text-[color:var(--np-brand,#B85F2A)] underline">report what you see</Link>.
              </p>
            ) : (
              <div className="mt-4 flex flex-wrap gap-2">
                {facets.facets.map((f) => (
                  <button
                    key={f.name}
                    onClick={() => drill(facets.level, f.name)}
                    data-testid={`farmers-local-facet-${f.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className="rounded-full border border-black/15 bg-[#FBF2E1]/50 px-3 py-1.5 text-sm hover:border-[color:var(--np-brand,#B85F2A)]"
                  >
                    {f.name} <span className="ml-1 font-mono-tech text-[10px] text-[#3A3830]">{f.count}</span>
                  </button>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Reports at this level */}
        <section className="mt-6" data-testid="farmers-local-reports">
          <div className="flex items-center gap-3">
            <h2 className="font-serif-display text-2xl">Observations here</h2>
            <span className="ml-auto font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">
              {reports.length} in past 6 months
            </span>
          </div>
          {loading ? (
            <div className="mt-4 h-32 rounded-2xl bg-black/5 animate-pulse" />
          ) : reports.length === 0 ? (
            <div className="mt-4 rounded-xl border border-dashed border-black/20 bg-white/70 p-8 text-center text-sm text-[#3A3830]">
              No observations at this level yet.
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              {reports.map((r) => (
                <Link
                  key={r.id}
                  to={`/observations/${r.id}`}
                  data-testid={`farmers-local-report-${r.id}`}
                  className="rounded-xl border border-black/10 bg-white p-4 hover:border-[color:var(--np-brand,#B85F2A)]"
                >
                  <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                    {r.category}{r.severity ? ` · ${r.severity}` : ""}{r.crop ? ` · ${r.crop}` : ""}
                  </div>
                  <div className="mt-1 font-serif-display text-lg leading-tight line-clamp-2">{r.title || "Observation"}</div>
                  {r.description && <p className="mt-1 text-xs text-[#3A3830] line-clamp-2">{r.description}</p>}
                  <div className="mt-2 text-[11px] text-[#3A3830] inline-flex items-center gap-1">
                    <MapPin size={10} weight="fill" />
                    {[r.location?.village, r.location?.district, r.location?.state, r.location?.country].filter(Boolean).join(", ")}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
